import React, { useState, useEffect, useRef, useMemo } from 'react';
import useBackGuard from '../hooks/useBackGuard';

const CATEGORIES_HAUT = ['As', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six'];
const CATEGORIES_BAS = [
  'Brelan', 'Carré', 'Petite suite', 'Grande suite', 'Full', 'Chance', 'Yams', 'Yams supplémentaire'
];

function YamsCalculator() {
  useBackGuard();

  const [players, setPlayers] = useState(() => {
    try {
      const saved = localStorage.getItem('yams-players');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [newPlayer, setNewPlayer] = useState('');
  const [showRankingPopup, setShowRankingPopup] = useState(false);
  const [hideControls, setHideControls] = useState(false);
  // Etat tampon pour la saisie des champs numériques (évite scroll prématuré)
  const [editingScores, setEditingScores] = useState({}); // key: "pIdx-idx" => valeur en cours
  // Nouveau: éviter réouverture forcée de la popup après fermeture manuelle
  const [rankingShownOnce, setRankingShownOnce] = useState(false);
  // Détection simplifiée mobile (iPhone/Android) pour garder le centrage sur mobile uniquement
  const isMobile = typeof navigator !== 'undefined' && /iPhone|Android/i.test(navigator.userAgent);

  const containerRef = useRef(null);
  const playerRefs = useRef([]);

  // Scroll uniquement horizontal sans changer la position verticale de la page
  const scrollCardHorizontally = (el, instant = false) => {
    if (!el || !containerRef.current) return;
    const container = containerRef.current;
    const behavior = instant ? 'auto' : 'smooth';
    if (isMobile) {
      // Comportement précédent (centrage) conservé sur mobile pour confort
      const targetLeft = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollTo({ left: Math.min(Math.max(0, targetLeft), maxScroll), behavior });
    } else {
      // Desktop: assurer seulement que la carte est entièrement visible (évite sur-décalage)
      const cardLeft = el.offsetLeft;
      const cardRight = cardLeft + el.clientWidth;
      const viewLeft = container.scrollLeft;
      const viewRight = viewLeft + container.clientWidth;
      if (cardLeft < viewLeft) {
        container.scrollTo({ left: cardLeft, behavior });
      } else if (cardRight > viewRight) {
        container.scrollTo({ left: cardRight - container.clientWidth, behavior });
      }
    }
  };

  const focusScoreElement = (el) => {
    if (!el) return;
    const prevY = window.scrollY;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
    // Toujours restaurer la position verticale si modifiée
    if (window.scrollY !== prevY) {
      window.scrollTo({ top: prevY });
    }
  };

  const addPlayer = () => {
    if (newPlayer.trim() && !players.find(p => p.name === newPlayer.trim())) {
      const prevY = window.scrollY;
      setPlayers([
        ...players,
        { name: newPlayer.trim(), scores: Array(CATEGORIES_HAUT.length + CATEGORIES_BAS.length).fill('') }
      ]);
      setNewPlayer('');
      setTimeout(() => {
        if (containerRef.current && playerRefs.current[players.length]) {
          scrollCardHorizontally(playerRefs.current[players.length]);
          // restaurer vertical
          window.scrollTo({ top: prevY });
        }
      }, 50);
    }
  };

  // updateScore avec paramètre autoAdvance pour contrôler le défilement automatique
  const updateScore = (playerIdx, catIdx, value, autoAdvance = true) => {
    setPlayers(prev => prev.map((p, idx) => {
      if (idx !== playerIdx) return p;
      const newScores = [...p.scores];
      newScores[catIdx] = value;
      return { ...p, scores: newScores };
    }));

    // Pas d'auto-avance si champ effacé ou si désactivé
    if (value === '' || !autoAdvance) return;

    const totalCategories = CATEGORIES_HAUT.length + CATEGORIES_BAS.length;
    const nextPlayerIdx = playerIdx + 1;

    if (nextPlayerIdx < players.length) {
      // joueur suivant, même catégorie
      setTimeout(() => {
        const nextCard = playerRefs.current[nextPlayerIdx];
        const nextEl = nextCard?.querySelector('[data-score-index="' + catIdx + '"]');
        if (nextEl) {
          scrollCardHorizontally(nextCard, true);
          // Sur mobile, focus sur un <select> l'ouvre immédiatement → on ne focus que les <input>
          if (nextEl.tagName !== 'SELECT') {
            focusScoreElement(nextEl);
          }
        }
      }, 30);
    } else {
      // dernier joueur → retour au premier joueur
      setTimeout(() => {
        if (containerRef.current) {
          // Scroll direct à 0 : fiable quel que soit le calcul d'offset
          containerRef.current.scrollTo({ left: 0, behavior: 'auto' });
        }
        // Tenter le focus sur la catégorie suivante si c'est un input
        const nextCatIdx = catIdx + 1;
        if (nextCatIdx < totalCategories) {
          const firstCard = playerRefs.current[0];
          if (firstCard) {
            const target = firstCard.querySelector('[data-score-index="' + nextCatIdx + '"]');
            if (target && target.tagName !== 'SELECT') {
              focusScoreElement(target);
            }
          }
        }
      }, 40);
    }
  };

  // Remet à zéro tous les scores
  const resetScores = () => {
    const prevY = window.scrollY;
    setPlayers(players.map(p => ({ ...p, scores: Array(CATEGORIES_HAUT.length + CATEGORIES_BAS.length).fill('') })));
    setTimeout(() => window.scrollTo({ top: prevY }), 0);
    setRankingShownOnce(false); // permettre réaffichage pour la prochaine partie
  };

  // Réinitialise la liste des joueurs
  const resetPlayers = () => {
    const prevY = window.scrollY;
    setPlayers([]);
    setNewPlayer('');
    localStorage.removeItem('yams-players');
    setTimeout(() => window.scrollTo({ top: prevY }), 0);
    setRankingShownOnce(false);
  };

  // Calcule le sous-total haut
  const getSousTotalHaut = (scores) => {
    return CATEGORIES_HAUT.reduce((sum, _, idx) => {
      const val = parseInt(scores[idx], 10);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  };

  // Calcule le sous-total bas
  const getSousTotalBas = (scores) => {
    return CATEGORIES_BAS.reduce((sum, _, idx) => {
      const val = parseInt(scores[idx + CATEGORIES_HAUT.length], 10);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  };

  // Calcule le bonus (63 à atteindre)
  const getBonus = (scores) => {
    const sousTotal = getSousTotalHaut(scores);
    return sousTotal >= 63 ? 35 : sousTotal - 63;
  };

  // Calcule le total global avec bonus éventuel
  const getTotal = (scores) => {
    const sousTotalHaut = getSousTotalHaut(scores);
    const sousTotalBas = getSousTotalBas(scores);
    const bonus = sousTotalHaut >= 63 ? 35 : 0;
    return sousTotalHaut + sousTotalBas + bonus;
  };

  // Génère les options pour chaque catégorie haute
  const getOptionsForHaut = (cat) => {
    const map = {
      'As': 1,
      'Deux': 2,
      'Trois': 3,
      'Quatre': 4,
      'Cinq': 5,
      'Six': 6
    };
    const val = map[cat];
    return [0, ...Array.from({ length: 5 }, (_, i) => val * (i + 1))];
  };

  // Vérifie si tous les champs obligatoires sont remplis pour un joueur
  const isPlayerComplete = (scores) => {
    for (let i = 0; i < scores.length - 1; i++) {
      if (scores[i] === '') return false;
    }
    return true;
  };

  const isGameComplete = useMemo(() => {
    return players.length > 0 && players.every(player => isPlayerComplete(player.scores));
  }, [players]);

  const getRanking = () => {
    return [...players]
      .map(player => ({
        name: player.name,
        score: getTotal(player.scores)
      }))
      .sort((a, b) => b.score - a.score)
      .map((player, index) => ({
        ...player,
        place: index + 1
      }));
  };

  // Validation centralisée d'un champ numérique bas (Brelan, Carré, Chance)
  const commitBasNumericScore = (playerIdx, catIdx, key, rawValue, options = {}) => {
    const { viaEnter = false, autoAdvance = true } = options;
    const trimmed = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();

    if (trimmed !== '') {
      updateScore(playerIdx, catIdx, trimmed, autoAdvance);
    } else {
      updateScore(playerIdx, catIdx, '', false);
    }

    // Nettoyage du tampon de saisie pour ce champ
    setEditingScores(prev => {
      const copy = { ...prev };
      delete copy[key];
      delete copy[`${key}-scrollLeft`];
      return copy;
    });
  };

  useEffect(() => {
    localStorage.setItem('yams-players', JSON.stringify(players));
  }, [players]);

  useEffect(() => {
    if (isGameComplete && !rankingShownOnce) {
      setShowRankingPopup(true);
      setRankingShownOnce(true);
    }
  }, [isGameComplete, rankingShownOnce]);

  // Fermeture via Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setShowRankingPopup(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="yams-calculator" style={{ position: 'relative' }}>
      {!hideControls && (
        <div style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, paddingBottom: 5 }}>
          <h1 style={{ marginTop: 0 }}>Calculateur de score Yams</h1>
          <div className="player-control-buttons" style={{ marginBottom: 12, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nom du joueur"
              value={newPlayer}
              onChange={e => setNewPlayer(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPlayer()}
              style={{ flex: '1', minWidth: '150px', maxWidth: '250px' }}
            />
            <button onClick={addPlayer}>Ajouter</button>
            <button onClick={resetScores} style={{ background: '#f59e42', color: 'white' }}>Effacer scores</button>
            <button onClick={resetPlayers} style={{ background: '#ef4444', color: 'white' }}>Réinitialiser joueurs</button>
            <button onClick={() => setHideControls(true)} style={{ background: '#111827', color: 'white' }}>Masquer commandes</button>
          </div>
        </div>
      )}
      {hideControls && (
        <div style={{ position: 'fixed', top: 8, right: 8, zIndex: 20, display: 'flex', gap: 8 }}>
          <button onClick={() => setHideControls(false)} style={{ background: '#111827', color: 'white' }}>Afficher commandes</button>
        </div>
      )}
      <div className="players-container" ref={containerRef} style={{
        display: 'flex',
        overflowX: 'auto',
        overflowY: 'hidden',
        gap: '15px',
        padding: '10px 0',
        flexWrap: 'nowrap',
        width: '100%',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {players.map((player, pIdx) => (
          <div ref={el => playerRefs.current[pIdx] = el} key={player.name} className="player-card" style={{
            minWidth: '280px',
            flex: '0 0 auto',
            maxWidth: '90%',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '4px 6px'
          }}>
            <h2 style={{ marginTop: 4 }}>{player.name}</h2>
            <table>
              <thead>
                <tr><th>Catégorie</th><th>Score</th></tr>
              </thead>
              <tbody>
                {CATEGORIES_HAUT.map((cat, cIdx) => (
                  <tr key={cat}>
                    <td>{cat}</td>
                    <td>
                      <select
                        data-score-index={cIdx}
                        value={player.scores[cIdx]}
                        onChange={e => updateScore(pIdx, cIdx, e.target.value, true)}
                        style={{ width: 70 }}
                      >
                        <option value="">--</option>
                        {getOptionsForHaut(cat).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
                  <td>Sous-total</td>
                  <td>{getSousTotalHaut(player.scores)}</td>
                </tr>
                <tr style={{ fontWeight: 'bold', background: getBonus(player.scores) === 35 ? '#dcfce7' : '#ffe0e0' }}>
                  <td>Bonus</td>
                  <td style={{ color: getBonus(player.scores) === 35 ? '#16a34a' : undefined }}>{getBonus(player.scores)}</td>
                </tr>
                {CATEGORIES_BAS.map((cat, cIdx) => {
                  const idx = cIdx + CATEGORIES_HAUT.length;
                  // Gestion des listes déroulantes et des limites
                  if (cat === 'Full') {
                    return (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td>
                          <select
                            data-score-index={idx}
                            value={player.scores[idx]}
                            onChange={e => updateScore(pIdx, idx, e.target.value, true)}
                            style={{ width: 70 }}
                          >
                            <option value="">--</option>
                            <option value="0">0</option>
                            <option value="25">25</option>
                          </select>
                        </td>
                      </tr>
                    );
                  } else if (cat === 'Petite suite') {
                    return (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td>
                          <select
                            data-score-index={idx}
                            value={player.scores[idx]}
                            onChange={e => updateScore(pIdx, idx, e.target.value, true)}
                            style={{ width: 70 }}
                          >
                            <option value="">--</option>
                            <option value="0">0</option>
                            <option value="30">30</option>
                          </select>
                        </td>
                      </tr>
                    );
                  } else if (cat === 'Grande suite') {
                    return (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td>
                          <select
                            data-score-index={idx}
                            value={player.scores[idx]}
                            onChange={e => updateScore(pIdx, idx, e.target.value, true)}
                            style={{ width: 70 }}
                          >
                            <option value="">--</option>
                            <option value="0">0</option>
                            <option value="40">40</option>
                          </select>
                        </td>
                      </tr>
                    );
                  } else if (cat === 'Brelan' || cat === 'Carré' || cat === 'Chance') {
                    const key = `${pIdx}-${idx}`;
                    return (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td>
                          <input
                            data-score-index={idx}
                            type="number"
                            min="0"
                            max="30"
                            value={editingScores[key] !== undefined ? editingScores[key] : player.scores[idx]}
                            onFocus={() => {
                              if (isMobile && containerRef.current) {
                                const prevLeft = containerRef.current.scrollLeft;
                                setEditingScores(prev => ({
                                  ...prev,
                                  [`${key}-scrollLeft`]: prevLeft
                                }));
                              }
                            }}
                            onChange={e => {
                              let v = e.target.value;
                              if (v !== '' && parseInt(v, 10) > 30) v = '30';
                              setEditingScores(prev => {
                                const copy = { ...prev, [key]: v };
                                if (isMobile && containerRef.current) {
                                  const storedLeft = prev[`${key}-scrollLeft`];
                                  if (typeof storedLeft === 'number' && containerRef.current.scrollLeft !== storedLeft) {
                                    containerRef.current.scrollLeft = storedLeft;
                                  }
                                }
                                return copy;
                              });
                            }}
                            onBlur={e => {
                              // Auto-avancer uniquement si l'utilisateur a effectivement saisi
                              // une valeur (onChange a été appelé). Sinon le champ a été focalisé
                              // programmatiquement (auto-avance) et son blur ne doit pas déclencher
                              // de nouvelle avance, surtout s'il contenait déjà une valeur.
                              commitBasNumericScore(pIdx, idx, key, e.target.value, {
                                viaEnter: false,
                                autoAdvance: editingScores[key] !== undefined
                              });
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                // Validation explicite au clavier (desktop principalement)
                                commitBasNumericScore(pIdx, idx, key, e.currentTarget.value, { viaEnter: true });
                              }
                            }}
                            style={{ width: 60 }}
                            inputMode="numeric"
                          />
                        </td>
                      </tr>
                    );
                  } else if (cat === 'Yams') {
                    return (
                      <tr key={cat}>
                        <td>{cat}</td>
                        <td>
                          <select
                            data-score-index={idx}
                            value={player.scores[idx]}
                            onChange={e => updateScore(pIdx, idx, e.target.value, true)}
                            style={{ width: 70 }}
                          >
                            <option value="">--</option>
                            <option value="0">0</option>
                            <option value="50">50</option>
                          </select>
                        </td>
                      </tr>
                    );
                  } else if (cat === 'Yams supplémentaire') {
                    // Yams supplémentaire : liste déroulante 50, 100, 150, 200
                    // Griser si le score du Yams est 0
                    const yamsIndex = CATEGORIES_HAUT.length + CATEGORIES_BAS.indexOf('Yams');
                    const yamsScore = player.scores[yamsIndex];
                    const isDisabled = yamsScore !== '50';

                    return (
                      <tr key={cat} style={{ opacity: isDisabled ? 0.5 : 1 }}>
                        <td>{cat}</td>
                        <td>
                          <select
                            data-score-index={idx}
                            value={isDisabled ? '' : player.scores[idx]}
                            onChange={e => updateScore(pIdx, idx, e.target.value, true)}
                            style={{ width: 90 }}
                            disabled={isDisabled}
                          >
                            <option value="">--</option>
                            {[50, 100, 150, 200].map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  } else {
                    // fallback (devrait jamais arriver)
                    return null;
                  }
                })}
                <tr style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
                  <td>Sous-total</td>
                  <td>{getSousTotalBas(player.scores)}</td>
                </tr>
                <tr style={{ fontWeight: 'bold', background: '#d0ffd0' }}>
                  <td>Total</td>
                  <td>{getTotal(player.scores)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Popup de classement */}
      {showRankingPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowRankingPopup(false); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) setShowRankingPopup(false); }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              minWidth: '280px',
              maxWidth: '400px',
              position: 'relative'
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowRankingPopup(false)}
              type="button"
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                background: 'none',
                border: 'none',
                fontSize: '26px',
                cursor: 'pointer',
                padding: '10px', // zone cliquable élargie
                lineHeight: '1',
                borderRadius: '6px'
              }}
              aria-label="Fermer classement"
            >
              ×
            </button>
            <h2 style={{ marginTop: '0', textAlign: 'center' }}>Classement final</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>Place</th>
                  <th style={{ textAlign: 'left' }}>Joueur</th>
                  <th style={{ textAlign: 'right' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {getRanking().map(player => (
                  <tr key={player.name}>
                    <td style={{ textAlign: 'center' }}>{player.place}</td>
                    <td style={{ textAlign: 'left' }}>{player.name}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{player.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default YamsCalculator;
